import { DataType } from "shared/types";
import { randomID, toSentenceCase } from "shared/utils";

import { DialogWithTrigger } from "features/components/Dialog";
import Link from "features/ui/Link";
import { MarkdownContent } from "features/ui/MarkdownContent";
import { ViaductLinkRegex } from "features/ui/MarkdownContent/ViaductLink/constants";
import MaterialTable, { RowData, SchemaEntry } from "features/ui/MaterialTable";

import { isJsonArray, isJsonArrayOfObjects } from "./utils";

interface SubTableProps {
  rows: Array<{ key: string; value: any }>;
}

interface ArrayListProps {
  title: string;
  values: string[];
}

export const prepareDuckTableValue = (key: string, value: any) => {
  if (isJsonArrayOfObjects(value)) {
    return <SubTable rows={JSON.parse(value)} />;
  } else if (isJsonArray(value)) {
    return <ArrayList title={key} values={JSON.parse(value)} />;
  } else if (Array.isArray(value)) {
    return <SubTable rows={value} />;
  } else if (
    value &&
    typeof value === "string" &&
    value.match(ViaductLinkRegex)
  ) {
    return <MarkdownContent content={value} />;
  }

  return value;
};

const prepareDuckSubTableData = (
  rows: Array<{ key: string; value: any }>
): RowData[] =>
  rows.map((row) => {
    const formattedRow: Record<string, any> = {};
    const keys = Object.keys(row);
    keys.forEach((key) => {
      const val = row[key as keyof typeof row];
      const value = prepareDuckTableValue(key, val);

      formattedRow[key] = value;
    });

    formattedRow["row_id"] = randomID();

    return formattedRow;
  });

const ArrayList = ({ title, values }: ArrayListProps) => {
  if (values.length === 0) return null;

  return (
    <DialogWithTrigger
      triggerElement={
        <Link to="#" id="subtable-trigger">
          {values.length}
        </Link>
      }
      title={toSentenceCase(title)}
    >
      <div className="p-5 space-y-2" key={toSentenceCase(title)}>
        <div className="max-h-[40rem] overflow-y-auto">
          {values.map((value, i) => (
            <MarkdownContent key={i} content={value} />
          ))}
        </div>
      </div>
    </DialogWithTrigger>
  );
};

const SubTable = ({ rows }: SubTableProps) => {
  if (rows.length === 0) return null;

  return (
    <DialogWithTrigger
      triggerElement={
        <Link to="#" id="subtable-trigger">
          {rows.length}
        </Link>
      }
    >
      <SubTableModal rows={rows} />
    </DialogWithTrigger>
  );
};

const SubTableModal = ({ rows }: SubTableProps) => {
  const columns = Object.keys(rows[0]);
  const schema: SchemaEntry[] = columns.map((col) => ({
    label: toSentenceCase(col),
    accessor: col,
    dataType: DataType.STRING,
  }));

  const data = prepareDuckSubTableData(rows);

  return (
    <MaterialTable
      schema={schema}
      data={data}
      pageKey="duck-sub-table"
      getRowId={({ row_id }) => row_id}
      hideFooter
      disableColumnMenu
      disableColumnSorting
    />
  );
};
